import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import ShikiCodeBlock from '../ShikiCodeBlock';
import CodeBlockToolbar from './CodeBlockToolbar';
import CsvPreview from './CsvPreview';
import HtmlPreview from './HtmlPreview';
import MarkdownPreview from './MarkdownPreview';
import './RichCodeBlock.css';

// Languages that have a Rich representation (everything else renders Raw only).
const RICH_LANGUAGES = new Set(['markdown', 'html', 'csv', 'tsv']);

/**
 * RichCodeBlock — the shared code-viewing primitive used across chat, posts,
 * and the file viewer. Every instance shares the exact same toolbar shape:
 *
 *   [language]          [Rich | Raw]  [📋 Copy]
 *
 * The toggle is always visible. For languages without a rich representation
 * (anything other than markdown, html or csv/tsv), the Rich option is disabled —
 * never hidden — so the UI is always consistent.
 *
 * The copy button is always visible and always copies the raw source.
 *
 * ## Variants
 *
 * - `variant="embedded"` (default) — a bordered, rounded block that floats
 *   inside prose (chat messages, posts, fenced blocks inside a rendered
 *   markdown document). Margin + max-height handled by the embedding surface.
 *
 * - `variant="full-bleed"` — the block fills its parent (100% height, no outer
 *   margin/border/radius). Used when RichCodeBlock IS the viewer, not an item
 *   inside one — e.g. the file viewer showing a raw code file, or showing a
 *   rendered markdown file (where the body slot holds the rendered prose).
 *
 * ## Rendered body
 *
 * - `markdown` + Rich → MarkdownPreview renders the content as rendered prose
 * - `html` + Rich     → HtmlPreview renders the content in a sandboxed iframe
 * - `csv`/`tsv` + Rich → CsvPreview renders a themed data grid (SheetJS parse)
 * - anything + Raw    → ShikiCodeBlock renders the syntax-highlighted source
 *
 * If a caller wants to render something custom under the toolbar (e.g. the
 * file viewer rendering its big "document view" of a markdown file with
 * centered prose), it can pass `children` — when present, children are
 * rendered in Rich mode instead of the built-in MarkdownPreview / HtmlPreview.
 * The raw Shiki view is still used for Raw mode (content is always what
 * `code` contains).
 */
const RichCodeBlock = ({
  code,
  language = 'text',
  showLineNumbers = false,
  maxLines = null,
  className = '',
  variant = 'embedded',
  defaultMode = 'raw',
  hideToggle = false,
  children,
}) => {
  const { isDarkMode } = useTheme();

  // Trim leading/trailing whitespace so Shiki doesn't render a blank line above
  // the first meaningful line of code (which pushes the code visually further
  // away from the toolbar than it should be).
  const trimmedCode = typeof code === 'string' ? code.replace(/^\s+|\s+$/g, '') : code;

  const normalizedLanguage = (language || 'text').toLowerCase();
  const richSupported = RICH_LANGUAGES.has(normalizedLanguage);

  // Initial mode respects both caller intent and language capability. If caller
  // asked for Rich on an unsupported language, fall back to Raw.
  const initialMode = richSupported && defaultMode === 'rich' ? 'rich' : 'raw';
  const [mode, setMode] = useState(initialMode);

  // If the language or defaultMode changes (e.g. FileViewer navigation between
  // files), re-evaluate the mode. Otherwise we could get stuck showing Raw on
  // a markdown file that defaulted to Rich.
  useEffect(() => {
    setMode(richSupported && defaultMode === 'rich' ? 'rich' : 'raw');
  }, [richSupported, defaultMode]);

  const renderBody = useMemo(() => {
    if (mode === 'raw') {
      return (
        <ShikiCodeBlock
          code={trimmedCode}
          language={language}
          showLineNumbers={showLineNumbers}
          maxLines={maxLines}
          showCopyButton={false}
          className="rich-code-block-inner-shiki"
        />
      );
    }
    // Rich mode
    if (children) {
      // Caller-supplied rich body (e.g. FileViewer's full document rendering).
      return children;
    }
    if (normalizedLanguage === 'markdown') {
      return <MarkdownPreview content={code} isDarkMode={isDarkMode} />;
    }
    if (normalizedLanguage === 'html') {
      return <HtmlPreview content={code} />;
    }
    if (normalizedLanguage === 'csv' || normalizedLanguage === 'tsv') {
      return <CsvPreview content={code} />;
    }
    // Defensive fallback — shouldn't happen because richSupported guards the
    // mode, but we return something sensible if ever reached.
    return (
      <ShikiCodeBlock
        code={trimmedCode}
        language={language}
        showLineNumbers={showLineNumbers}
        showCopyButton={false}
        className="rich-code-block-inner-shiki"
      />
    );
  }, [mode, code, trimmedCode, language, normalizedLanguage, showLineNumbers, maxLines, children, isDarkMode]);

  const variantClass =
    variant === 'full-bleed' ? 'rich-code-block--full-bleed' : 'rich-code-block--embedded';

  return (
    <div
      className={`rich-code-block ${variantClass} ${className} ${isDarkMode ? 'dark' : 'light'} mode-${mode}`}
    >
      <CodeBlockToolbar
        language={normalizedLanguage}
        richSupported={richSupported}
        hideToggle={hideToggle}
        mode={mode}
        onModeChange={setMode}
        rawCode={code}
      />
      <div className="rich-code-block-body">{renderBody}</div>
    </div>
  );
};

export default RichCodeBlock;
